
import React, { useState, useEffect } from "react";
import { Poll } from "@/types/poll";
import { DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import PollBasicInfoFields from "./form/PollBasicInfoFields";
import PollCategoryStatusFields from "./form/PollCategoryStatusFields";
import PollLocationTimeFields from "./form/PollLocationTimeFields";
import PollOptionsEditor from "./form/PollOptionsEditor";
import PollImageUpload from "./form/PollImageUpload";
import PollAnonymousVotingField from "./form/PollAnonymousVotingField";
import PollPublicVotingField from "./form/PollPublicVotingField";
import PollPointsToggle from "./PollPointsToggle";
import useCategories from "@/hooks/useCategories";

interface PollFormProps {
  currentPoll: Poll | null;
  setCurrentPoll: React.Dispatch<React.SetStateAction<Poll | null>>;
  onCancel: () => void;
  onSave: () => void;
  isEditing: boolean;
}

const PollForm: React.FC<PollFormProps> = ({
  currentPoll,
  setCurrentPoll,
  onCancel,
  onSave,
  isEditing,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const isMobile = useIsMobile();
  const { categories } = useCategories(true);
  const [initialSync, setInitialSync] = useState(true);

  // Helper function to update field values
  const updateField = (field: keyof Poll, value: any) => {
    if (!currentPoll) return;
    setCurrentPoll({ ...currentPoll, [field]: value });
  };

  // Sync category_id and category name when categories are loaded
  useEffect(() => {
    if (currentPoll && categories.length > 0 && initialSync) {
      let updatedPoll = { ...currentPoll };
      let needsUpdate = false;
      
      // If we have category_id but no category name
      if (updatedPoll.category_id && (!updatedPoll.category || updatedPoll.category === '')) {
        const category = categories.find(cat => cat.id === updatedPoll.category_id);
        if (category) {
          updatedPoll.category = category.name;
          needsUpdate = true;
        }
      }
      // If we have category name but no category_id
      else if (updatedPoll.category && (!updatedPoll.category_id || updatedPoll.category_id === '')) {
        const category = categories.find(cat => cat.name === updatedPoll.category);
        if (category) {
          updatedPoll.category_id = category.id;
          needsUpdate = true;
        }
      }
      
      if (needsUpdate) {
        console.log('Syncing category data:', { 
          name: updatedPoll.category, 
          id: updatedPoll.category_id 
        });
        setCurrentPoll(updatedPoll);
      }
      
      setInitialSync(false);
    }
  }, [currentPoll, categories, setCurrentPoll, initialSync]);

  const validatePoll = (): boolean => {
    if (!currentPoll) return false;
    if (!currentPoll.title.trim()) {
      toast.error("Title is required");
      return false;
    }
    if (!currentPoll.description.trim()) {
      toast.error("Description is required");
      return false;
    }
    if (!currentPoll.category_id) {
      toast.error("Category is required");
      return false;
    }
    if (currentPoll.options.length < 2) {
      toast.error("At least two options are required");
      return false;
    }
    if (currentPoll.options.some((option) => !option.text.trim())) {
      toast.error("All options must have text");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePoll()) return;
    setIsSaving(true);
    try {
      await onSave();
    } catch (error) {
      console.error("Error saving poll:", error);
      toast.error("Failed to save poll");
    } finally {
      setIsSaving(false);
    }
  };

  if (!currentPoll) return null;

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4 max-h-[70vh] overflow-y-auto px-1"
    >
      <div className="space-y-4 pb-4">
        {/* Basic Info Section */}
        <PollBasicInfoFields
          currentPoll={currentPoll}
          updateField={updateField}
        />
        
        {/* Options Editor */}
        <PollOptionsEditor
          currentPoll={currentPoll}
          setCurrentPoll={setCurrentPoll}
        />
        
        {/* Category and Status */}
        <PollCategoryStatusFields
          currentPoll={currentPoll}
          updateField={updateField}
          isEditing={isEditing}
        />
        
        {/* Anonymous Voting Toggle */}
        <PollAnonymousVotingField
          currentPoll={currentPoll}
          updateField={updateField}
        />
        
        {/* Public Voting Toggle */}
        <PollPublicVotingField
          currentPoll={currentPoll}
          updateField={updateField}
        />
        
        {/* Points Toggle - Only show for existing polls (when editing) */}
        {isEditing && currentPoll.id && (
          <PollPointsToggle
            pollId={currentPoll.id}
            pollTitle={currentPoll.title}
            disabled={isSaving}
          />
        )}
        
        {/* Location and Time */}
        <PollLocationTimeFields
          currentPoll={currentPoll}
          updateField={updateField}
        />
        
        {/* Image Upload */}
        <PollImageUpload
          currentPoll={currentPoll}
          updateField={updateField}
        />
      </div>
      
      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isSaving}>
          {isSaving ? "Saving..." : isEditing ? "Update Poll" : "Create Poll"}
        </Button>
      </DialogFooter>
    </form>
  );
};

export default PollForm;
